import { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

/**
 * Imperative aria-live announcer for sb-customizer.
 *
 * Mount <AnnouncerProvider> once near the app root; consumers call useAnnouncer()
 * to push messages to the polite or assertive live region.
 *
 * Usage:
 *
 *   // At app root (e.g., index.js):
 *   <AnnouncerProvider>
 *     <App />
 *   </AnnouncerProvider>
 *
 *   // In a component:
 *   const announce = useAnnouncer();
 *   announce(__('Loaded 12 more posts', 'sb-customizer'));
 *   announce(__('Save failed', 'sb-customizer'), { assertive: true });
 *
 * Notes:
 *   - The 50ms delay before setting the message text is intentional. Screen
 *     readers ignore live regions that already contain the new text; clearing
 *     first then re-setting forces re-announcement when the same message is
 *     announced twice in a row.
 *   - Polite messages are queued (role="status", aria-live="polite") and
 *     announced when the SR is idle. Assertive messages (role="alert",
 *     aria-live="assertive") interrupt. Use assertive sparingly — only for
 *     errors and critical state changes.
 *
 * Reference: .pitch/wcag-accessibility-audit/react-canonical-pattern.md (PR 1).
 */
const AnnouncerContext = createContext(null);

export function AnnouncerProvider({ children }) {
    const [politeMsg, setPoliteMsg] = useState('');
    const [assertiveMsg, setAssertiveMsg] = useState('');
    const timerRef = useRef(null);

    useEffect(() => () => clearTimeout(timerRef.current), []);

    const announce = useCallback((message, { assertive = false } = {}) => {
        if (!message) return;
        clearTimeout(timerRef.current);
        if (assertive) setAssertiveMsg('');
        else setPoliteMsg('');
        timerRef.current = setTimeout(() => {
            if (assertive) setAssertiveMsg(String(message));
            else setPoliteMsg(String(message));
        }, 50);
    }, []);

    return (
        <AnnouncerContext.Provider value={announce}>
            {children}
            <div
                role="status"
                aria-live="polite"
                aria-atomic="true"
                className="sb-visually-hidden"
            >
                {politeMsg}
            </div>
            <div
                role="alert"
                aria-live="assertive"
                aria-atomic="true"
                className="sb-visually-hidden"
            >
                {assertiveMsg}
            </div>
        </AnnouncerContext.Provider>
    );
}

export function useAnnouncer() {
    const ctx = useContext(AnnouncerContext);
    if (!ctx) {
        if (process.env.NODE_ENV !== 'production') {
            console.warn(
                '[sb-customizer] useAnnouncer called outside <AnnouncerProvider>. Announcements will be no-ops.'
            );
        }
        return () => {};
    }
    return ctx;
}
